import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Preferences } from '@capacitor/preferences'
import { setLocale, type SupportedLocale } from '@/services/i18n/i18n'
import { defaultLocale, supportedLocales } from '@/locales'
import { Capacitor } from '@capacitor/core'
import { reregisterDeviceToken } from '@/services/notifications/device-token-manager'

const LOCALE_STORAGE_KEY = 'user_locale'

export const useLocaleStore = defineStore('locale', () => {
    const currentLocale = ref<SupportedLocale>(defaultLocale)

    /**
     * Initialize locale from storage
     */
    async function initLocale() {
        try {
            const { value } = await Preferences.get({ key: LOCALE_STORAGE_KEY })
            if (value && isValidLocale(value)) {
                currentLocale.value = value as SupportedLocale
                setLocale(currentLocale.value)
            } else {
                // Try to detect preferred locale from device or browser
                const preferred = await detectPreferredLocale()
                if (preferred) {
                    currentLocale.value = preferred
                    setLocale(currentLocale.value)
                    await saveLocale(currentLocale.value)
                }
            }
        } catch (error) {
            console.error('Failed to load locale from storage:', error)
        }
    }

    /**
     * Change the application locale
     */
    async function changeLocale(locale: SupportedLocale) {
        if (!isValidLocale(locale)) {
            console.error(`Invalid locale: ${locale}`)
            return
        }

        currentLocale.value = locale
        setLocale(locale)
        await saveLocale(locale)

        // Re-register device token with updated locale so server can send translated notifications
        try {
            await reregisterDeviceToken()
            console.log('[LocaleStore] Device token re-registered with new locale')
        } catch (error) {
            console.error('[LocaleStore] Failed to re-register device token:', error)
        }
    }

    /**
     * Save locale to storage
     */
    async function saveLocale(locale: SupportedLocale) {
        try {
            await Preferences.set({
                key: LOCALE_STORAGE_KEY,
                value: locale
            })
        } catch (error) {
            console.error('Failed to save locale to storage:', error)
        }
    }

    /**
     * Detect browser locale and map to supported locale
     */
    async function detectPreferredLocale(): Promise<SupportedLocale | null> {
        try {
            let langTag: string | null = null

            // Use Capacitor Device API on native platforms
            if (Capacitor.getPlatform() !== 'web') {
                try {
                    // Vite: avoid pre-bundling requirement if plugin is not installed
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    const { Device } = await import(/* @vite-ignore */ '@capacitor/device')
                    const { value } = await Device.getLanguageTag()
                    langTag = value || null
                } catch {
                    try {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        const { Device } = await import(/* @vite-ignore */ '@capacitor/device')
                        const { value } = await Device.getLanguageCode()
                        langTag = value || null
                    } catch {
                        langTag = null
                    }
                }
            }

            // Fallback to browser language on web or if native detection failed
            if (!langTag && typeof navigator !== 'undefined' && navigator.language) {
                langTag = navigator.language
            }

            if (!langTag) {
                return null
            }

            // Normalize to base language code, e.g., 'en-US' -> 'en'
            const base = langTag.toLowerCase().split('-')[0]

            // Return supported locale if available
            if (isValidLocale(base)) {
                return base as SupportedLocale
            }

            return null
        } catch (e) {
            console.warn('[LocaleStore] Failed to detect preferred locale:', e)
            return null
        }
    }

    /**
     * Validate if locale is supported
     */
    function isValidLocale(locale: string): boolean {
        return supportedLocales.some(l => l.code === locale)
    }

    /**
     * Get current locale code
     */
    function getCurrentLocale(): SupportedLocale {
        return currentLocale.value
    }

    return {
        currentLocale,
        initLocale,
        changeLocale,
        getCurrentLocale
    }
})
